/**
 * Trade Performance Centre – Learner Login
 * Same dashboard as main learners, but only the trading course is shown.
 * Uses sessionStorage 'tpcLearner' so Courses.js can filter to trading course only.
 */
import React, { useState, startTransition } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "../../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { setHeroUser } from "../../../redux/actions";
import {
  isLearnerImpersonationPassword,
  signInLearnerWithImpersonationPassword,
} from "../../../utils/learnerImpersonationLogin";
import LoadingCircleFacilitator from "../../utils/LoadingCircleFacilitator";
import tpcAndUSLogo from "../../../images/AndUsLoginLogo.png";
import { useTradeGoogleTagManager } from "../../../utils/tradeGoogleTagManager";
import {
  getRoleClaims,
  hasDashboardClaim,
} from "../../../utils/roleUtils";
import "./HeroLoginManual.css";

const seedHeroUserFromUserRecords = async (email, dispatch) => {
  const fn = getFunctions();
  const readDocSafe = httpsCallable(fn, "firestoreReadDocSafe");
  const primaryCompany = "TPC_Group";
  const collectionBrand = "TechPerformanceCentre";

  const tryPath = async (companyKey, brandKey) => {
    const res = await readDocSafe({
      company: companyKey,
      collectionBrand: brandKey,
      systemName: "Users",
      uniqueId: email,
    });
    return res.data;
  };

  let result = await tryPath(primaryCompany, collectionBrand);
  if (!result.found) result = await tryPath(primaryCompany, "shared");
  const userDoc = result.found ? result.data : {};

  let userName = "";
  try {
    const valRes = await readDocSafe({
      company: "TPC_Group",
      collectionBrand: "TechPerformanceCentre",
      systemName: "userValidation",
      uniqueId: email,
    });
    const doc = valRes?.data;
    userName = doc?.name || "";
  } catch {}

  let userGroupDetails = {};
  if (userDoc?.userGroup && userDoc.userGroup !== "Public") {
    try {
      const groupRes = await readDocSafe({
        company: primaryCompany,
        collectionBrand,
        systemName: "UserGroups",
        uniqueId: userDoc.userGroup,
      });
      if (groupRes.data.found) {
        const groupData = groupRes.data.data;
        userGroupDetails = {
          groupName: groupData.groupName,
          hierarchy: groupData.hierarchy,
          roles: groupData.roles || [],
          permissions: groupData.permissions || [],
          settings: groupData.settings || {},
        };
      }
    } catch {}
  }

  dispatch(
    setHeroUser({
      uid: auth.currentUser?.uid || "",
      email,
      name: userName,
      company: primaryCompany,
      collectionBrand,
      userGroup: userDoc.userGroup || "",
      userRole: userDoc.role || "",
      userGroupDetails,
      userGroupPermissions: userGroupDetails.permissions || [],
      userGroupSettings: userGroupDetails.settings || {},
    })
  );
};

export default function TPCLearnerLogin({
  applicationScope = "trade",
  loginTitle = "Trade Performance Centre Login",
  welcomeText = "Trade Performance Centre",
  learnerFlag = true,
  registerRoute = "/trade-register",
  alternateLoginRoute = null,
  alternateLoginLabel = "",
  enableTradeGoogleTagManager = false,
}) {
  useTradeGoogleTagManager(enableTradeGoogleTagManager);

  const [email, setEmail] = useState("");
  const [password, setPw] = useState("");
  const [error, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const nav = useNavigate();
  const dispatch = useDispatch();

  const applyScopedApplicationFlagAndNavigate = (
    userProfile,
    hasLearnerAccessClaim = false,
  ) => {
    sessionStorage.setItem("applicationScope", applicationScope);
    if (learnerFlag) {
      sessionStorage.setItem("tpcLearner", "true");
    } else {
      sessionStorage.removeItem("tpcLearner");
    }
    sessionStorage.setItem("learnerJustLoggedIn", "1");

    if (
      !hasLearnerAccessClaim &&
      (userProfile?.state === "Applicant" || !userProfile?.isCompleted)
    ) {
      startTransition(() => nav("/learner-welcome-home"));
    } else {
      startTransition(() => nav("/learner-dashboard"));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");
    setBusy(true);

    try {
      sessionStorage.removeItem("learnerImpersonationActive");

      const usingImpersonation = isLearnerImpersonationPassword(password);
      if (usingImpersonation) {
        await signInLearnerWithImpersonationPassword(email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);

        if (!auth.currentUser.emailVerified)
          throw new Error("Please verify your e-mail first.");
      }

      const fn = getFunctions();
      const readDocSafe = httpsCallable(fn, "firestoreReadDocSafe");
      const profileResult = await readDocSafe({
        company: "TPC_Group",
        collectionBrand: "TechPerformanceCentre",
        systemName: "Applicants",
        uniqueId: email,
      });

      const userProfile = profileResult.data?.data;
      const { claims } = await auth.currentUser.getIdTokenResult(true);
      const roleClaims = getRoleClaims(claims);
      const hasLearnerAccessClaim = hasDashboardClaim(roleClaims, "learner");
      console.info("[LearnerLogin] Firebase role claims:", roleClaims);
      if (!hasLearnerAccessClaim && !userProfile) {
        throw new Error(
          "Your account does not have Learner Dashboard access.",
        );
      }

      await seedHeroUserFromUserRecords(email, dispatch);
      applyScopedApplicationFlagAndNavigate(
        userProfile,
        hasLearnerAccessClaim,
      );
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setErr("");
    setSuccess("");
    setBusy(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      if (!user?.email) throw new Error("Google sign-in failed.");
      if (!user.emailVerified)
        throw new Error("Please verify your e-mail first.");
      const { claims } = await user.getIdTokenResult(true);
      const roleClaims = getRoleClaims(claims);
      const hasLearnerAccessClaim = hasDashboardClaim(roleClaims, "learner");
      console.info("[LearnerLogin] Firebase role claims:", roleClaims);

      const fn = getFunctions();
      const readDocSafe = httpsCallable(fn, "firestoreReadDocSafe");
      const firestoreOperations = httpsCallable(fn, "firestoreOperations");

      const profileResult = await readDocSafe({
        company: "TPC_Group",
        collectionBrand: "TechPerformanceCentre",
        systemName: "Applicants",
        uniqueId: user.email,
      });

      const profileFound = profileResult.data?.found;
      const userProfile = profileResult.data?.data;

      // If profile doesn't exist, create one with incomplete status
      if (!profileFound || !userProfile) {
        const displayName = user.displayName || user.email.split("@")[0];

        await firestoreOperations({
          operation: "write",
          company: "TPC_Group",
          collectionBrand: "TechPerformanceCentre",
          systemName: "Applicants",
          uniqueId: user.email,
          documentData: {
            email: user.email,
            fullName: displayName,
            photoURL: user.photoURL || "",
            uid: user.uid,
            isCompleted: false,
            applicationScope,
            discipline: applicationScope === "ai-professional"
              ? "AI in the workplace"
              : "Trade",
            authProvider: "google",
            emailVerified: true,
          },
        });

        await seedHeroUserFromUserRecords(user.email, dispatch);
        applyScopedApplicationFlagAndNavigate(
          { state: "Applicant", isCompleted: false },
          hasLearnerAccessClaim,
        );
        return;
      }

      await seedHeroUserFromUserRecords(user.email, dispatch);
      applyScopedApplicationFlagAndNavigate(
        userProfile,
        hasLearnerAccessClaim,
      );
    } catch (e) {
      if (e?.code === "auth/popup-closed-by-user") {
        setErr("Sign-in cancelled. Please try again.");
      } else if (e?.code === "auth/popup-blocked") {
        setErr("Pop-up blocked. Please enable pop-ups and try again.");
      } else {
        setErr(e.message || "Google sign-in failed.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess("Password reset email sent. Please check your inbox.");
    } catch (err) {
      setErr(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleForgotPassword = () => {
    setErr("");
    setSuccess("");
    setForgotPassword((prev) => !prev);
  };

  if (busy) return <LoadingCircleFacilitator />;

  return (
    <div className="hl-container">
      <div className="hl-left">
        <div className="hl-logo-wrap">
          <img src={tpcAndUSLogo} alt="TPC" className="hl-logo" />
        </div>
        <div className="hl-welcome-wrap">
          <div className="hl-welcome-text">
            {welcomeText}
          </div>
        </div>
      </div>

      <div className="hl-right">
        <form
          className="hl-form"
          onSubmit={forgotPassword ? handleForgotPassword : handleLogin}
        >
          <h2 className="hl-title">
            {forgotPassword
              ? "Reset Password"
              : loginTitle}
          </h2>
          {error && <p className="hl-error">{error}</p>}
          {success && <p className="hl-success">{success}</p>}
          <input
            type="email"
            placeholder="Email"
            className="hl-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {!forgotPassword && (
            <input
              type="password"
              placeholder="Password"
              className="hl-input"
              value={password}
              onChange={(e) => setPw(e.target.value)}
              required
            />
          )}
          <button className="hl-button" type="submit">
            {forgotPassword ? "Send Reset Email" : "Login"}
          </button>

          {!forgotPassword && (
            <>
              <div className="hl-divider">
                <span>or</span>
              </div>
              <button
                type="button"
                className="hl-button hl-google-button"
                onClick={handleGoogleSignIn}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9.003 18z"
                    fill="#34A853"
                  />
                  <path
                    d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
                    fill="#EA4335"
                  />
                </svg>
                Sign in with Google
              </button>
            </>
          )}

          <div className="hl-forgot-password">
            <button
              type="button"
              className="hl-forgot-toggle"
              onClick={toggleForgotPassword}
              style={{
                background: "none",
                border: "none",
                color: "#003366",
                fontWeight: 600,
                cursor: "pointer",
                padding: 0,
              }}
            >
              {forgotPassword ? "Back to Login" : "Forgot Password?"}
            </button>
          </div>
        </form>
        <div style={{ marginTop: 24, textAlign: "center" }}>
          Not registered?{" "}
          <Link
            to={registerRoute}
            style={{ fontWeight: 600, color: "#003366" }}
          >
            Sign up here
          </Link>
        </div>
        {alternateLoginRoute && (
          <div style={{ marginTop: 12, textAlign: "center" }}>
            Looking for a different application?{" "}
            <Link
              to={alternateLoginRoute}
              style={{ fontWeight: 600, color: "#003366" }}
            >
              {alternateLoginLabel}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
